import { Loading } from "@alm/flow-ds/components";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ListGroup, ListGroupItem } from "components/ListGroup";
import PageHeader from "components/PageHeader";
import PaginationButton from "components/PaginationButton";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import usePagedData from "hooks/usePagedData";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import VariablesHeading from "./VariablesHeading";
import VariableCard from "./VariableCard";

interface Props {
	getVariables: (
		id: string,
		params?: { limit: number; offset: number }
	) => Promise<{
		hasMore: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		results: any;
	}>;
	entityId: string;
}

const VariablesTable = ({ getVariables, entityId }: Props) => {
	const { t } = useTranslation();

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const { state, setData, ready, addPage, loading } = usePagedData();
	/**
	 * Load instances on mount
	 */
	useEffect(() => {
		if (!init) {
			getVariables(entityId)
				.then(results => {
					if (_isMounted.current) {
						setData(results);
						ready();
						setInit(true);
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_isMounted, ready, entityId, getVariables]);

	useEffect(() => {
		if (init) {
			loading();
			// Load activities from API with search query
			getVariables(entityId, {
				limit: 20,
				offset: 0,
			})
				.then(results => {
					if (_isMounted.current) {
						setData(results);
						ready();
					}
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_isMounted, loading, ready]);

	const page = useCallback(() => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Instances page",
		});
		loading();
		getVariables(entityId, {
			limit: state.limit,
			offset: state.offset,
		})
			.then(res => {
				addPage(res);
				ready();
			})
			.catch(e => console.error(e));
	}, [
		loading,
		getVariables,
		entityId,
		state.limit,
		state.offset,
		addPage,
		ready,
	]);
	if (!init) return <Loading className="mt-4 elem__loader--md" />;

	return (
		<>
			<PageHeader title={t("main.variables.title")} />
			{state.results.length ? (
				<>
					<VariablesHeading />
					<ListGroup className="list-group-links">
						{state.results.map((variable, index: number) => (
							<ListGroupItem key={`variable__${index}`}>
								<div className="list-group-card-link variables__card">
									<VariableCard {...variable} />
								</div>
							</ListGroupItem>
						))}
					</ListGroup>
				</>
			) : (
				!state.loading && (
					<p className="no-variables">
						{t("main.variables.text.noVariables")}
					</p>
				)
			)}
			{state.hasMore ? (
				<PaginationButton
					pageType="variables"
					loading={state.loading}
					page={page}
					hasMore={state.hasMore}
				/>
			) : (
				<div className="mb-50"></div>
			)}
		</>
	);
};

export default VariablesTable;
